(ns minorodata.components.home-dashboard.transforms
  (:require
   ["date-fns" :refer [differenceInSeconds format parseISO]]
   ["date-fns-tz" :refer [toZonedTime]]
   ["lucide-react" :refer [LayoutListIcon]]
   ["react" :as react]
   [minorodata.app.utils :refer [format-date]]
   [minorodata.components.home-dashboard.shared :refer [chart-colors duration-formatter
                                                          format-duration mini-stat stat-card]]
   [minorodata.components.spinner.core :as spinner]
   [re-frame.core :as re-frame]
   [recharts :refer [Bar BarChart CartesianGrid Legend Pie
                     PieChart Tooltip XAxis YAxis]]
   [reitit.frontend.easy :as rt.easy]
   [ui.card :as card]
   [ui.hover-card :as hover-card]
   [ui.table :as table]
   [ui.typography :as typography]))

(defn transforms-active-card []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-transforms])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-transforms])]
    [hover-card/hover-card-root
     [hover-card/hover-card-trigger {:class "block text-left"}
      [mini-stat
       {:icon       [:> LayoutListIcon]
        :icon-class (str
                     "bg-transforms/20 "
                     "[&>svg]:text-transforms")
        :title      "Transforms"
        :value      (:total data)
        :loading    loading?}]]
     [hover-card/hover-card-content
      [typography/h4 {:class "mb-4"} "Scheduled vs Non-scheduled transforms"]
      [:> PieChart {:data [{:name "Scheduled" :value (get data :scheduled) :fill (nth chart-colors 0)}
                           {:name "Non-scheduled" :value (get data :non-scheduled) :fill (nth chart-colors 1)}]
                    :width 300
                    :height 300}
       [:> Tooltip]
       [:> Legend]
       [:> Pie {:dataKey       "value"
                :nameKey       "name"
                ;; :cx            "50%"
                ;; :cy            "50%"
                ;; :outerRadius   80
                :label         true
                :labelLine     false
                :isAnimationActive true}]]]]))

(defn transforms-in-queue-card []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-activity])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-activity])]
    [stat-card {:title "Transforms in queue"
                :value (:queued data)
                :loading loading?}]))

(defn transforms-in-queue-stat []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-activity])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-activity])]
    [mini-stat {:title "In queue"
                :value (:queued data)
                :loading loading?}]))

(defn running-transform-row [{:keys [transform]}]
  (let [[duration set-duration] (react/useState 0)]
    (react/useEffect
     (fn []
       (let [interval-id (js/setInterval
                          #(set-duration
                            (differenceInSeconds (toZonedTime (js/Date.) "UTC") (parseISO (:started-at transform))))
                          1000)]
         (fn []
           (js/clearInterval interval-id))))
     #js [])

    [table/tr
     [table/td (:transform-name transform)]
     [table/td (format-date (:started-at transform))]
     [table/td (.format duration-formatter #js {:minutes (js/Math.floor (/ duration 60))
                                                :seconds (js/Math.floor (mod duration 60))})]]))

(defn transforms-running-card []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-activity])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-activity])]
    [hover-card/hover-card-root
     [hover-card/hover-card-trigger {:class "text-left"}
      [stat-card {:title "Running transforms"
                  :hoverable true
                  :value (:running data)
                  :loading loading?}]]
     [hover-card/hover-card-content
      (if (empty? (:running-details data))
        [typography/h4 "No transforms are currently running."]
        [table/table
         [table/thead
          [table/tr
           [table/th "Transform"]
           [table/th "Started at"]
           [table/th "Duration"]]]
         [table/tbody
          (for [t (:running-details data)]
            [:f> running-transform-row {:key (:transform-id t)
                                        :transform t}])]])]]))

(defn transforms-running-stat []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-activity])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-activity])]
    [hover-card/hover-card-root
     [hover-card/hover-card-trigger {:class "block text-left"}
      [mini-stat {:title "Running"
                  :value (:running data)
                  :loading loading?}]]
     [hover-card/hover-card-content
      (if (empty? (:running-details data))
        [typography/h4 "No transforms are currently running."]
        [table/table
         [table/thead
          [table/tr
           [table/th "Transform"]
           [table/th "Started at"]
           [table/th "Duration"]]]
         [table/tbody
          (for [t (:running-details data)]
            [:f> running-transform-row {:key (:transform-id t)
                                        :transform t}])]])]]))

(defn transforms-finished-card []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-transforms])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-transforms])]
    [stat-card {:title "Finished transform runs"
                :value (get-in data [:finished :current])
                :prev-value (get-in data [:finished :previous])
                :loading loading?}]))

(defn transforms-finished-chart []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-transforms])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-transforms])]
    [card/card {:class "rounded-lg"}
     [card/card-content {:class "p-6"}
      [card/card-title {:class "mb-6"}
       "Transform runs finished over time"]
      [:div
       (if loading?
         [:div {:class "w-full flex items-center justify-center"}
          [spinner/with-text {:text "Loading transforms runtime chart..."}]]

         [:> BarChart {:width  "100%"
                       :height 300
                       :data   (or (:execution-timeline data) [])}
          [:> CartesianGrid {:vertical false}]
          [:> XAxis {:dataKey "period"
                     :axis-line false
                     :tick-formatter #(format % "d MMM yy HH:mm")}]
          [:> YAxis {:type "number"
                     :axis-line false
                     :tick-line false}]

          [:> Bar {:dataKey "succeeded" :fill "var(--color-emerald-500)" :stack-id "finished" :name "Succeeded"}]
          [:> Bar {:dataKey "failed" :fill "var(--color-red-500)" :stack-id "finished" :name "Failed"}]
          [:> Tooltip {:label-formatter #(format % "d MMM yy HH:mm")}]
          [:> Legend]])]]]))

(defn transforms-slowest-table []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-transforms])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-transforms])]
    [card/card {:class "rounded-lg"}
     [card/card-content {:class "p-6"}
      [card/card-title {:class "mb-6"}
       "Slowest 10 transform runs"]
      [:div {:class "overflow-x-auto"}
       (if loading?
         [:div {:class "w-full flex items-center justify-center"}
          [spinner/with-text {:text "Loading slowest transform runs..."}]]

         [table/table {:class "min-w-full"}
          [table/thead
           [table/tr
            [table/th "Run time"]
            [table/th "Transform"]
            [table/th "Group name"]
            [table/th "Start time"]
            [table/th "End time"]]]
          [table/tbody
           (for [row (:slowest data)]
             [table/tr {:key (str (:transform-id row) (:started-at row))}
              [table/td {:class "whitespace-nowrap"}
               (format-duration (:runtime-seconds row))]
              [table/td {:class "whitespace-nowrap"}
               [:a {:class "text-kleene-text-link hover:underline"
                    :href (rt.easy/href :transforms {:group-id (:group-id row)})}
                (:transform-name row)]]
              [table/td {:class "whitespace-nowrap"} (:group-name row)]
              [table/td {:class "whitespace-nowrap"}
               (format-date (parseISO (:started-at row)))]
              [table/td {:class "whitespace-nowrap"}
               (format-date (parseISO (:finished-at row)))]])]])]]]))

(defn recently-failed-transforms-table []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-transforms])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-transforms])]
    [card/card {:class "rounded-lg"}
     [card/card-content {:class "p-6"}
      [card/card-title {:class "mb-6"}
       "Most recent 10 transform run failures"]
      [:div {:class "overflow-x-auto"}
       (if loading?
         [:div {:class "w-full flex items-center justify-center"}
          [spinner/with-text {:text "Loading recently failed transforms..."}]]

         [table/table {:class "min-w-full"}
          [table/thead
           [table/tr
            [table/th "Failure time"]
            [table/th "Transform"]
            [table/th "Group name"]]]
          [table/tbody
           (for [row (:recently-failed data)]
             [table/tr {:key (str (:transform-id row) (:failed-at row))}
              [table/td {:class "whitespace-nowrap"} (format-date (:failed-at row))]
              [table/td {:class "whitespace-nowrap"}
               [:a {:class "text-kleene-text-link hover:underline"
                    :href (rt.easy/href :log {} {:source "job-manager"
                                                 :term   (:transform-name row)})}
                (:transform-name row)]]
              [table/td {:class "whitespace-nowrap"} (:group-name row)]])]])]]]))

(defn transforms-runtime-distribution-chart []
  (let [data @(re-frame/subscribe [:analytics.subs/summary-transforms])
        loading? @(re-frame/subscribe [:mold-http/loading? :analytics.api/fetch-transforms])]
    [card/card {:class "rounded-lg"}
     [card/card-content {:class "p-6"}
      [card/card-title {:class "mb-6"}
       "Transforms runtime distribution"]
      [:div
       (if loading?
         [:div {:class "w-full flex items-center justify-center"}
          [spinner/with-text {:text "Loading transforms distribution..."}]]

         [:> BarChart {:width  "100%"
                       :height 300
                       :data   (or (:distribution data) [])}
          [:> CartesianGrid {:vertical false}]
          [:> Bar {:dataKey "avg-runtime-seconds"
                   :name "Average runtime (s)"
                   :fill "var(--color-sky-500)" :radius 5}]
          [:> YAxis {:type "number"
                     :axis-line false
                     :tick-line false
                     :tick-formatter (fn [s] (str s "s"))}]
          [:> XAxis {:dataKey "transform-name"
                     :axis-line false
                     :tick false}]
          [:> Tooltip]])]]]))
